<template>
    <div>
        <EChart
            :options="chartOptions"
            card
            :header="$t('averageAvailabilityByProducts')"
            overlay
            :loading="loading"
            :error="request.getError()"
            :tooltip="$t('tooltip')"
            @click="handleChartClick"
        >
            <template #nav>
                <div class="d-flex gap-3 flex-column">
                    <div class="d-flex gap-3">
                        <DropdownFilter v-model="currentNumberShown" :items="[10, 20, 50, null]" :prefix="`${$t('show')}: `" :disabled="loading" />
                        <DropdownFilter v-model="currentSortBy" :items="['averageDesc', 'averageAsc', 'changeDesc', 'changeAsc']" :prefix="`${$t('sortBy')}: `" :disabled="loading" />
                    </div>
                    <div class="ml-auto d-flex gap-3 justify-content-end align-items-center">
                        <b-form-input v-model="filterSearch" class="width-200" :placeholder="$t('search')" :disabled="loading" />
                    </div>
                </div>
            </template>
        </EChart>
        <ModalAverageAvailabilityByProductsItem />
    </div>
</template>



<script>
    import sum from 'lodash/sum';
    import camelCase from 'camelcase';
    import { calcPercentDiff } from '@/utils/number';
    import EChart from '@/components/common/EChart.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';
    import ModalAverageAvailabilityByProductsItem from './ModalAverageAvailabilityByProductsItem.vue';

    export default {
        name: 'AverageAvailabilityByProducts',
        components: {
            EChart,
            DropdownFilter,
            ModalAverageAvailabilityByProductsItem,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.parentComponent.requestData,
                requestCompare: this.parentComponent.requestDataCompare,
                currentNumberShown: 10,
                currentSortBy: 'averageDesc',
                filterSearch: null,
            };
        },
        computed: {
            loading() {
                return this.request.getLoading() || this.requestCompare.getLoading();
            },
            data() {
                return this.request.getData() || {};
            },
            dataCompare() {
                return this.requestCompare.getData() || {};
            },
            chartData() {
                return this.computeChartData(this.data);
            },
            chartDataCompare() {
                return this.computeChartData(this.dataCompare);
            },
            filteredChartData() {
                if (this.filterSearch) {
                    return this.chartData.filter(item => item.product.toLowerCase().includes(this.filterSearch.toLowerCase()));
                }
                return this.chartData;
            },
            sortedChartData() {
                if (this.loading) return {};

                const compareDictionary = {};
                this.chartDataCompare.forEach(compareItem => {
                    compareDictionary[compareItem.product] = compareItem.avgAvailability;
                });

                const baseData = this.filteredChartData;
                const dataWithDiff = baseData.map(productItem => {
                    const compareValue = compareDictionary[productItem.product] || 0;
                    const difference = calcPercentDiff(productItem.avgAvailability, compareValue);
                    return { ...productItem, diff: difference };
                });

                let sortedData;
                switch (this.currentSortBy) {
                    case 'averageDesc':
                        sortedData = [...dataWithDiff].sort((first, second) => second.avgAvailability - first.avgAvailability);
                        break;
                    case 'averageAsc':
                        sortedData = [...dataWithDiff].sort((first, second) => first.avgAvailability - second.avgAvailability);
                        break;
                    case 'changeDesc':
                        sortedData = [...dataWithDiff].sort((first, second) => second.diff - first.diff);
                        break;
                    case 'changeAsc':
                        sortedData = [...dataWithDiff].sort((first, second) => first.diff - second.diff);
                        break;
                    default:
                        sortedData = dataWithDiff;
                }

                return this.currentNumberShown ? sortedData.slice(0, this.currentNumberShown) : sortedData;
            },
            chartOptions() {
                if (!this.sortedChartData.length) {
                    return { series: [] };
                }

                const productLabels = this.sortedChartData.map(product => ({
                    value: product.product,
                    metaData: product,
                }));
                const availabilityValues = this.sortedChartData.map(product => product.avgAvailability);
                const differences = this.sortedChartData.map(product => product.diff);
                const availabilitySummary = sum(availabilityValues);

                return {
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: { type: 'shadow' },
                    },
                    xAxis: {
                        type: 'value',
                        max: 'dataMax',
                    },
                    yAxis: [
                        {
                            type: 'category',
                            inverse: true,
                            data: productLabels,
                            axisLabel: {
                                formatter: labelValue => ` {btnIcon|}${labelValue}`,
                            },
                            triggerEvent: true,
                        },
                        {
                            type: 'category',
                            inverse: true,
                            data: productLabels,
                            position: 'right',
                            axisLine: { show: false },
                            axisTick: { show: false },
                            axisLabel: {
                                rich: {
                                    pos: {
                                        color: 'green',
                                        backgroundColor: 'rgba(0,255,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                    zero: {
                                        color: '#666',
                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                    neg: {
                                        color: 'red',
                                        backgroundColor: 'rgba(255,0,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                },
                                formatter: (labelValue, index) => {
                                    const diffValue = differences[index];
                                    const percentLabel = this.$format.percent(diffValue);
                                    if (diffValue > 0) return `{pos|▲${percentLabel}}`;
                                    if (diffValue < 0) return `{neg|▼${percentLabel}}`;
                                    return `{zero|${percentLabel}}`;
                                },
                            },
                        },
                    ],
                    series: [
                        {
                            type: 'bar',
                            stack: 'total',
                            label: {
                                show: true,
                                position: 'insideLeft',
                                formatter: params => {
                                    const barValue = params.data.value;
                                    return `${this.$format.percent((barValue * 100) / availabilitySummary, 2)} (${barValue})`;
                                },
                            },
                            data: availabilityValues.map(value => ({ value })),
                        },
                    ],
                };
            },
        },
        methods: {
            pascalCase(text) {
                return camelCase(text, { pascalCase: true });
            },
            computeChartData(sourceData) {
                const eventsByProduct = {};
                Object.values(sourceData).forEach(productObj => {
                    const productName = productObj.product.name;
                    if (!eventsByProduct[productName]) {
                        eventsByProduct[productName] = [];
                    }
                    const charts = productObj.charts;
                    Object.keys(charts).forEach(chartKey => {
                        charts[chartKey].forEach(chartObj => {
                            const segments = chartObj.chart.segments;
                            if (segments && segments.length) {
                                segments.forEach(segment => {
                                    eventsByProduct[productName].push({ time: segment.start, delta: +1 });
                                    eventsByProduct[productName].push({ time: segment.end, delta: -1 });
                                });
                            }
                        });
                    });
                });

                const productSummaries = [];
                for (const productName in eventsByProduct) {
                    const productEvents = eventsByProduct[productName];
                    const groupedEvents = {};
                    productEvents.forEach(event => {
                        groupedEvents[event.time] = (groupedEvents[event.time] || 0) + event.delta;
                    });

                    const eventTimes = Object.keys(groupedEvents)
                        .map(timeKey => Number(timeKey))
                        .sort((a, b) => a - b);

                    if (eventTimes.length === 0) continue;

                    let cumulativeCount = 0;
                    let weightedSum = 0;
                    let totalDuration = 0;
                    let previousTime = eventTimes[0];

                    eventTimes.forEach(currentTime => {
                        const duration = currentTime - previousTime;
                        weightedSum += cumulativeCount * duration;
                        totalDuration += duration;
                        cumulativeCount += groupedEvents[currentTime];
                        previousTime = currentTime;
                    });

                    const averageAvailability = totalDuration ? weightedSum / totalDuration : cumulativeCount;
                    productSummaries.push({
                        product: productName,
                        avgAvailability: Math.round(averageAvailability),
                    });
                }

                productSummaries.sort((a, b) => b.avgAvailability - a.avgAvailability);
                return productSummaries;
            },
            mergeIntervals(segmentList) {
                if (!segmentList?.length) return [];
                const sortedSegments = [...segmentList]
                    .map(segment => ({ start: Number(segment.start), end: Number(segment.end) }))
                    .filter(segment => Number.isFinite(segment.start) && Number.isFinite(segment.end) && segment.end >= segment.start)
                    .sort((a, b) => a.start - b.start);

                const mergedSegments = [sortedSegments[0]];
                for (let i = 1; i < sortedSegments.length; i += 1) {
                    const lastSegment = mergedSegments[mergedSegments.length - 1];
                    const currentSegment = sortedSegments[i];
                    if (currentSegment.start <= lastSegment.end) {
                        lastSegment.end = Math.max(lastSegment.end, currentSegment.end);
                    } else {
                        mergedSegments.push({ ...currentSegment });
                    }
                }
                return mergedSegments;
            },
            buildAllPriceOccurrencesForProduct(productName) {
                const occurrencesByShop = {};
                Object.values(this.data).forEach(productObj => {
                    if (productObj?.product?.name !== productName) return;

                    const charts = productObj?.charts || {};
                    Object.keys(charts).forEach(chartKey => {
                        charts[chartKey].forEach(chartObj => {
                            const shopName = chartObj?.offer?.campaign || '—';
                            const segments = chartObj?.chart?.segments || [];
                            if (!occurrencesByShop[shopName]) occurrencesByShop[shopName] = [];
                            segments.forEach(segment => {
                                const start = Number(segment?.start);
                                const end = Number(segment?.end);
                                if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
                                    occurrencesByShop[shopName].push({ start, end });
                                }
                            });
                        });
                    });
                });

                Object.keys(occurrencesByShop).forEach(shopName => {
                    occurrencesByShop[shopName] = this.mergeIntervals(occurrencesByShop[shopName]);
                    if (!occurrencesByShop[shopName].length) delete occurrencesByShop[shopName];
                });

                return occurrencesByShop;
            },
            handleChartClick(params) {
                if (params.componentType === 'yAxis') {
                    const axisData = this.chartOptions.yAxis[1].data;
                    const metaData = axisData?.[params.dataIndex]?.metaData;
                    if (!metaData) return;

                    const allPriceOccurrences = this.buildAllPriceOccurrencesForProduct(metaData.product);
                    this.showDetails({ ...metaData, allPriceOccurrences });
                }
            },
            showDetails(item) {
                const modalId = 'ModalAverageAvailabilityByProductsItem';
                this.$root.$emit('bv::show::modal', modalId);
                this.$root.$emit(modalId, item);
            },
            resetFilters() {
                this.currentNumberShown = 10;
                this.currentSortBy = 'averageDesc';
                this.filterSearch = null;
            },
        },
        created() {
            this.parentComponent.parentComponent.$on('resetFilters', this.resetFilters);
        },
        beforeDestroy() {
            this.parentComponent.parentComponent.$off('resetFilters', this.resetFilters);
        },
    };
</script>



<i18n lang="json" locale="pl" scope="local">
{
    "tooltip": "Wykres prezentuje ranking produktów według średniej dostępności ofert produktów producenta w sklepach wyświetlanych w widgecie (ustawienia domyślnie). Aby sprawdzić liczbę ofert w pozostałych sklepach, użyj filtra Sklep."
}
</i18n>

<i18n lang="json" locale="en" scope="local">
{
    "tooltip": "The chart presents the ranking of products according to the average availability of manufacturer's products offers in stores displayed in the widget (settings by default). To check the number of offers in other stores, use the shop filter."
}
</i18n>
